import { useCallback, useMemo, useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { AssignmentsDistributionPicker } from 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentsDistributionPicker'
import {
  FormattedAssignment,
  FormattedAssignmentWithDeletion,
} from 'modules/cost_centers/components/CostCenterAssigments/lib/types'
import { AssignmentsChange, ResourceDistribution } from 'modules/cost_centers/lib/types'

interface AssignmentPickerProps {
  resourceDistribution: ResourceDistribution
  resourceDate: string
  onChange?: (value: { assignments: AssignmentsChange }) => void
  readonly?: boolean
}

export const AssignmentPicker = ({
  resourceDistribution,
  resourceDate,
  readonly,
  onChange,
}: AssignmentPickerProps) => {
  const assignmentsFromDistribution = useMemo(
    () =>
      resourceDistribution
        .filter((distribution) => !distribution.allocationId)
        .map((distribution) => ({
          id: distribution.assignmentId,
          startDate: resourceDate,
          endDate: resourceDate,
          costCenter: {
            ...distribution.costCenter,
            dimensions: [],
          },
          percentage: distribution.percentage,
        })),
    [resourceDistribution, resourceDate]
  )
  const [percentage, setPercentage] = useState(
    assignmentsFromDistribution.reduce((acc, distribution) => acc + distribution.percentage, 0)
  )

  const handleAssignmentsChange = useCallback(
    (values: {
      assignmentsToCreate: FormattedAssignment[]
      assignmentsToUpdate: FormattedAssignmentWithDeletion[]
    }) => {
      const assignmentsToCreate = values.assignmentsToCreate.map((assignment) => ({
        costCenterId: assignment.costCenterId,
        percentage: assignment.percentage,
        startDate: resourceDate,
        endDate: resourceDate,
      }))

      const assignmentsToUpdate = values.assignmentsToUpdate.map((assignment) => ({
        id: Number(assignment.id),
        costCenterId: assignment.costCenterId,
        percentage: assignment.percentage,
        startDate: resourceDate,
        endDate: resourceDate,
        markedForDeletion: !!assignment.markedForDeletion,
      }))

      onChange?.({
        assignments: {
          assignmentsToCreate,
          assignmentsToUpdate,
        },
      })

      setPercentage(
        assignmentsToCreate.reduce((acc, assignment) => acc + assignment.percentage, 0) +
          assignmentsToUpdate
            .filter((assignment) => !assignment.markedForDeletion)
            .reduce((acc, assignment) => acc + assignment.percentage, 0)
      )
    },
    [onChange, resourceDate]
  )

  const showPercentageWarning = percentage != 1 && percentage > 0

  return (
    <>
      <AssignmentsDistributionPicker
        assignments={assignmentsFromDistribution}
        onChange={handleAssignmentsChange}
        defaultDateRange={{
          startDate: resourceDate,
          endDate: resourceDate,
        }}
        readonly={readonly}
      />
      {showPercentageWarning && !readonly && (
        <Box paddingTop="s8">
          <Box
            paddingY="s12"
            paddingX="s16"
            background="criticalBackground"
            borderRadius={{ all: 'abs016' }}
            gap="s8"
            flexDirection="row"
            alignItems="center"
          >
            <Icon.Medium icon="alert-circle-line" color="criticalForegroundPrimary" />
            <Text.Normal color="grey1000">
              {i18n.t('finance.cost_centers.assignments.error_percentage')}
            </Text.Normal>
          </Box>
        </Box>
      )}
    </>
  )
}
